import React, {
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useRef,
} from "react"
import { createRoot } from "react-dom/client"
import { ErrorBoundary } from "@sentry/react"
import { useUpdateEffect } from "ahooks"
import anime from "animejs"
import classNames from "classnames"
import {
    LandscapeDisplay,
    SafeAreaProvider,
    useLandscapeDisplayInfo,
} from "@hg/layouts"
import { useShowRoleSelect } from "@hg/one-account"

import "@/utils/sentry"

import Layout from "@/components/Layout"
import EventBus from "@/utils/eventBus"
import { handleResizeBySize } from "@/utils/misc"
import { collect } from "@/utils/sdk"
import PageGame from "./pages/Game"
import { useGameStore } from "./pages/Game/store"
import PagePreview from "./pages/Preview"
import { LoginView } from "./pages/Registry/LoginView"
import { battleMusic, gameMusic, music } from "./utils/music"
import {
    EPage,
    ESubPage,
    GlobalContext,
    globalReducer,
    initialGlobalState,
    IReducer,
} from "./context"

import "./theme/global.less"
import styles from "./index.module.less"

const pages = [
    {
        key: EPage.Preview,
        component: PagePreview,
    },
]

const subPages = [
    {
        key: ESubPage.Game,
        component: PageGame,
    },
]

const App = () => {
    const {
        state: { show, currentSubPage, showRegistry, bgm, user },
        dispatch,
    } = useContext(GlobalContext)

    useEffect(() => {
        if (bgm) {
            if (currentSubPage === ESubPage.Game) {
                if (useGameStore.getState().battleMusic) {
                    battleMusic.enable()
                } else {
                    gameMusic.enable()
                }
            } else {
                music.enable()
            }
        } else {
            gameMusic.disable()
            battleMusic.disable()
            music.disable()
        }
    }, [bgm])

    const pageRef = useRef<HTMLDivElement>(null)
    const maskRef = useRef<HTMLDivElement>(null)

    const animeRef = useRef<anime.AnimeInstance | null>(null)

    const { width, height } = useLandscapeDisplayInfo()
    useEffect(() => {
        if (!width || !height) {
            return
        }

        const fontSize = handleResizeBySize(width, height, 16)
        dispatch({
            type: "updateContext",
            payload: { show: true, fontSize },
        })
    }, [dispatch, height, width])

    const showOrExitSubPage = useCallback(
        (key: ESubPage, show: boolean) => {
            const subPageElement = document.querySelector(
                `.${key}`
            ) as HTMLDivElement
            const subPages = [
                ...document.querySelectorAll(`.${styles.subPage}`),
            ] as HTMLDivElement[]

            animeRef.current?.pause()

            if (key === ESubPage.Game && bgm) {
                if (show) {
                    music.disable()
                    if (useGameStore.getState().battleMusic) {
                        battleMusic.enable()
                    } else {
                        gameMusic.enable()
                    }
                } else {
                    music.enable()
                    gameMusic.disable()
                    battleMusic.disable()
                }
            }

            if (show) {
                animeRef.current = anime
                    .timeline()
                    .add({
                        targets: maskRef.current,
                        opacity: 1,
                        duration: 500,
                        easing: "easeOutSine",
                        complete: () => {
                            subPageElement.classList.add(styles.show)
                            for (const subPage of subPages) {
                                if (subPage !== subPageElement) {
                                    subPage.classList.remove(styles.show)
                                }
                            }
                        },
                    })
                    .add({
                        targets: maskRef.current,
                        opacity: 0,
                        duration: 500,
                        easing: "easeInSine",
                        complete: () => {
                            animeRef.current = null
                        },
                    })
            } else {
                animeRef.current = anime
                    .timeline()
                    .add({
                        targets: maskRef.current,
                        opacity: 1,
                        duration: 500,
                        easing: "easeOutSine",
                        complete: () => {
                            subPageElement.classList.remove(styles.show)
                            dispatch({ type: "closeSubPage" })
                        },
                    })
                    .add({
                        targets: maskRef.current,
                        opacity: 0,
                        duration: 500,
                        easing: "easeInSine",
                        complete: () => {
                            animeRef.current = null
                        },
                    })
            }
        },
        [bgm, dispatch]
    )

    useUpdateEffect(() => {
        if (currentSubPage) {
            showOrExitSubPage(currentSubPage, true)
        }
    }, [currentSubPage])

    useEffect(() => {
        if (!user) {
            return
        }

        // 埋点
        collect("user_view", {
            channel_id: user.channelId,
            game_uid: user.uid,
        })
    }, [user])
    const showRoleSelect = useShowRoleSelect()

    useEffect(() => {
        EventBus.subscribe("showRoleSelect", showRoleSelect)

        return () => {
            EventBus.unsubscribe("showRoleSelect", showRoleSelect)
        }
    }, [showRoleSelect])

    return (
        <div className={classNames(styles.app, show && styles.show)}>
            <div ref={pageRef} className={styles.page}>
                {pages.map((page, index) => (
                    <page.component
                        key={page.key}
                        className={page.key}
                        style={{
                            zIndex: 10 - index,
                            opacity: 1,
                        }}
                    />
                ))}
            </div>
            {subPages.map((page) => (
                <page.component
                    key={page.key}
                    className={classNames(styles.subPage, page.key)}
                    visible={currentSubPage === page.key}
                    onExit={() => showOrExitSubPage(page.key, false)}
                />
            ))}
            <LoginView visible={showRegistry} className={styles.pageRegistry} />
            <div ref={maskRef} className={styles.mask} />
        </div>
    )
}

const Root: React.FC = () => {
    const [globalState, dispatch] = useReducer<IReducer>(
        globalReducer,
        initialGlobalState
    )

    return (
        <ErrorBoundary>
            <GlobalContext.Provider
                value={{
                    state: globalState,
                    dispatch,
                }}
            >
                <Layout
                    context={{
                        type: globalState.type,
                        dispatch,
                    }}
                >
                    <LandscapeDisplay>
                        <SafeAreaProvider>
                            <App />
                        </SafeAreaProvider>
                    </LandscapeDisplay>
                </Layout>
            </GlobalContext.Provider>
        </ErrorBoundary>
    )
}

const container = document.getElementById("root")
if (container) {
    const root = createRoot(container)
    root.render(<Root />)
}
